import { LocalizedInputWithLabel } from '@components/common/LocalizedInputWithLabel';
import CheckboxInputT from '@components/tailwind-components/Input/CheckboxInputT';
import ColorInputWithLabelT from '@components/tailwind-components/Input/ColorInputWithLabelT';
import {
  getVoiceMessages,
  VOICE_TEXT_FIELDS,
  type VoiceTextField,
  type VoiceTextGroup,
} from '@conversed/shared/src/central/constants/voice-messages';
import { type Control, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import TextsGroup from './TextsGroup';

interface VoiceTextsFieldsProps {
  control: Control<any>;
  // The call screen texts, or the tab bar texts with the NEW badge.
  part: 'callScreen' | 'tabs';
  // Keeps input ids unique when several configs are open on one page.
  idPrefix: string;
  inputClassName?: string;
  labelClassName?: string;
  checkboxLabelClassName?: string;
}

const isNewBadgeField = (field: VoiceTextField) =>
  field.group === 'tabs' && field.key === 'newBadge';

const isCallScreenField = (field: VoiceTextField) =>
  field.group === 'status' || field.group === 'buttons';

// One voice group of the webclient texts. Which studio sees it is decided by
// the caller.
const VoiceTextsFields: React.FC<VoiceTextsFieldsProps> = ({
  control,
  part,
  idPrefix,
  inputClassName = '',
  labelClassName = '',
  checkboxLabelClassName = '',
}) => {
  const { t } = useTranslation();
  const voiceLanguage = useWatch({ control, name: 'options.voice.language' });
  const isNewBadgeEnabled = useWatch({
    control,
    name: 'options.voice.newBadge.enabled',
  });

  const builtInMessages = getVoiceMessages(voiceLanguage) as unknown as Record<
    VoiceTextGroup,
    Record<string, string>
  >;

  const callScreenFields = VOICE_TEXT_FIELDS.filter(isCallScreenField);
  const tabFields = VOICE_TEXT_FIELDS.filter(
    (field) => field.group === 'tabs' && !isNewBadgeField(field),
  );
  const newBadgeField = VOICE_TEXT_FIELDS.find(isNewBadgeField);

  // An empty optional text is hidden, so showing the built-in text there would
  // suggest a fallback that does not happen.
  const getPlaceholder = (field: VoiceTextField, label: string) => {
    if (!field.isRequired) {
      return label;
    }
    return builtInMessages[field.group][field.key] || label;
  };

  const renderField = (field: VoiceTextField) => {
    const path = `${field.group}.${field.key}`;
    const label = t(
      `settings.webclient.config.form.config.voice-texts.${path}`,
    );

    return (
      <div key={path}>
        <LocalizedInputWithLabel
          control={control}
          field={`options.voice.texts.${path}`}
          id={`${idPrefix}-voiceText-${field.group}-${field.key}`}
          label={label}
          placeholder={getPlaceholder(field, label)}
          required={
            field.isRequired ? t('general.validation.required') : undefined
          }
          inputClassName={inputClassName}
          labelClassName={labelClassName}
        />
        {!field.isRequired && (
          <p className='text-pico text-gray-400 -mt-1 mb-2'>
            {t('settings.webclient.config.form.config.voice-texts.empty-hides')}
          </p>
        )}
      </div>
    );
  };

  if (part === 'callScreen') {
    return (
      <TextsGroup
        title={t('settings.webclient.config.form.texts-group.voice-call')}
        icon='mic'>
        {callScreenFields.map(renderField)}
      </TextsGroup>
    );
  }

  return (
    <TextsGroup
      title={t('settings.webclient.config.form.texts-group.tabs')}
      icon='tab'
      description={t(
        'settings.webclient.config.form.texts-group.tabs.description',
      )}>
      {tabFields.map(renderField)}
      <CheckboxInputT
        label={t(
          'settings.webclient.config.form.config.voice-new-badge.enabled',
        )}
        control={control}
        field='options.voice.newBadge.enabled'
        id={`${idPrefix}-voiceNewBadgeEnabled`}
        labelClassName={checkboxLabelClassName}
      />
      {isNewBadgeEnabled && newBadgeField && (
        <div className='flex flex-col gap-1 mt-1'>
          {renderField(newBadgeField)}
          <ColorInputWithLabelT
            field='options.voice.newBadge.backgroundColor'
            label={t(
              'settings.webclient.config.form.config.voice-new-badge.background-color',
            )}
            id={`${idPrefix}-voiceNewBadgeBackgroundColor`}
            control={control}
            mainClasses='justify-between'
            hexInputClassName='text-xs'
            labelClassName='text-xs'
          />
          <ColorInputWithLabelT
            field='options.voice.newBadge.textColor'
            label={t(
              'settings.webclient.config.form.config.voice-new-badge.text-color',
            )}
            id={`${idPrefix}-voiceNewBadgeTextColor`}
            control={control}
            mainClasses='justify-between'
            hexInputClassName='text-xs'
            labelClassName='text-xs'
          />
        </div>
      )}
    </TextsGroup>
  );
};

export default VoiceTextsFields;
